/**
 * TeacherPartTwo component represents the second part of the teacher form.
 * It handles form submission, data fetching, and dynamic table row management.
 *
 * @param {TFormProps} props - The properties passed to the component.
 * @param {boolean} props.disabled - Indicates if the form fields should be disabled.
 * @param {string} props.currentFormId - The current form ID.
 * @param {string} props.currentUfmId - The current user form mapping ID.
 *
 * @returns {JSX.Element} The rendered TeacherPartTwo component.
 *
 * @component
 *
 * @example
 * <TeacherPartTwo disabled={false} currentFormId="encryptedFormId" currentUfmId="encryptedUfmId" />
 *
 * @remarks
 * This component uses various hooks and context to manage form state, fetch data, and handle form submission.
 * It includes dynamic table row management for multiple sections of the form.
 *
 * @see {@link useFetch} for data fetching.
 * @see {@link useToast} for toast notifications.
 * @see {@link UserContext} for user context.
 * @see {@link LanguageContext} for language context.
 * @see {@link LoaderContext} for loader context.
 */
import { useContext, useEffect, useState } from "react";
import styles from "../../../form.module.css";
import { DatePicker, Image } from "antd";

import { Button, Col, Flex, Form, Input, Row, Select, Typography } from "antd";
import useFetch from "../../../../../hooks/useFetch";
import useToast from "../../../../../hooks/useToast";
import { eDocType, eFORMSTATUS, eResultCode } from "../../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../../state/toast/slice";
import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import Text from "../../../../../context/language-context";
import { Table } from "antd";
import type { TableProps } from "antd";
import { FaCheck } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../../../../context";
import {
  GetFormData,
  updateField,
  UploadDocs,
} from "../../../../../utils/api.constant";
import AuthUtil from "../../../../../utils/auth";
import { TFormProps } from "../../../../../types";
import { LoaderContext } from "../../../../../context/loader";
import { Rule } from "antd/es/form";
import {
  disableFutureYears,
  getNumberInLanguage,
} from "../../../../../utils/helperFunctions";
import {
  DATA_TYPE_TEACHER_PT2_Q10,
  DATA_TYPE_TEACHER_PT2_Q11,
  DATA_TYPE_TEACHER_PT2_Q12,
  DATA_TYPE_TEACHER_PT2_Q13,
  DATA_TYPE_TEACHER_PT2_Q14,
  DATA_TYPE_TEACHER_PT2_Q15,
  DATA_TYPE_TEACHER_PT2_Q16,
  DATA_TYPE_TEACHER_PT2_Q9,
} from "../../../../../types/table_types";
import EncryptUtils from "../../../../../utils/encrypt";
import dayjs from "dayjs";
import Utils from "../../../../../utils";
import {
  allowOnlyNumericInput_onKeyDown,
  disableFutureDates_on_input_date_max,
  numberInputRules,
  validateFile,
} from "../../../../../utils/input.validator";

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const TeacherPartTwo = ({
  disabled,
  currentFormId,
  currentUfmId,
}: TFormProps) => {
  const [form] = Form.useForm();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const navigate = useNavigate();
  const [formId, setFormId] = useState<string | null>(null);
  const [ufmId, setUfmId] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<string | null>(null);
  const { user } = useContext(UserContext);
  const [documentsData, setDocumentsData] = useState<any>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [signatureId, setSignatureId] = useState<string | null>(null);
  const [applyPdfStyles, setApplyPdfStyles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState<Boolean>(false);

  // Loader Context For show Loader
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;

  const [rowCount, setRowCount] = useState({
    TEACHER_PT2_Q5: 5,
    TEACHER_PT2_Q9: 1,
    TEACHER_PT2_Q10: 1,
    TEACHER_PT2_Q11: 1,
    TEACHER_PT2_Q12: 1,
    TEACHER_PT2_Q13: 1,
    TEACHER_PT2_Q14: 1,
    TEACHER_PT2_Q15: 1,
    TEACHER_PT2_Q16: 1,
  });

  const inputRequiredRule: Rule[] = [
    {
      required: true,
      message: Text({ tid: "REQUIRED", def: "Required" }),
    },
  ];

  useEffect(() => {
    const documentFields = [{ id: signatureId, field: "TEACHER_PT2_Q19" }];

    documentFields.forEach(({ id, field }) => {
      if (id) {
        handleFieldChange(field, id);
      }
    });
  }, [signatureId]);

  const handleFieldChange = async (
    field: string,
    value: any,
    bulkPayload?: any,
    isSubmitted?: boolean
  ) => {
    if (formId?.length && ufmId?.length) {
      try {
        const payload: any = {
          data: {
            id: formId,
            userFormMappingId: ufmId,
          },
        };
        if (field.length > 0) {
          const trimmedValue = value.trim();
          if (trimmedValue.length) {
            payload.data[field] = trimmedValue || "";
          } else {
            form.setFieldValue(field, "");
            return;
          }
        }
        if (bulkPayload) {
          payload.data = {
            ...payload.data,
            ...bulkPayload,
          };
        }
        if (isSubmitted) {
          setIsSubmitting(true);
          payload.data.isSubmitted = true;
        }
        const response = await post(updateField, payload);
        const { dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setIsLoading(false);
          if (isSubmitted) {
            onShowToast({
              type: ToastType.success,
              title: <FaCheck />,
              position: ToastOpen.leftBottom,
              content: "Form Submitted Successfully!",
            });
            setIsSubmitting(false);
            setTimeout(() => {
              navigate("/");
            }, 1000);
          }
        } else if (returnCode == eResultCode.INVALID_REQUEST) {
          onShowToast({
            type: ToastType.error,
            title: <ExclamationCircleOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const uploadDocument = async (
    file: File,
    type: eDocType,
    documentName?: string
  ) => {
    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("docType", type.toString());
      formData.append("ufmId", ufmId || "");
      formData.append("userId", user?.id?.toString() || "");
      formData.append("labelName", documentName || "");
      formData.append("currentStage", eFORMSTATUS.STAGE2INPROGRESS.toString());
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDocs}`,
        {
          method: "POST",
          body: formData,
          headers: { ...AuthUtil.getToken() },
        }
      );
      const res = await response.json();
      const { data, dataResponse } = res;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        setIsUploading(false);

        // Handle specific document uploads
        if (documentName === "TEACHER_PT2_Q19") {
          setSignatureId(data.docUrl.toString());
        }
        return data.docUrl.toString();
      } else {
        setIsUploading(false);

        console.error("Failed to upload images", response.statusText);
      }
    } catch (error) {
      setIsUploading(false);

      console.error(error);
    }
  };

  // Helper function to generate properly formatted Q5 data
  const generateCorrectQ5Data = (existingData?: any[]) => {
    return Array.from({ length: 5 }, (_, index) => {
      const existingRow = existingData?.[index] || {};
      const numericYear = new Date().getFullYear() - index;
      return {
        key: index,
        year: numericYear.toString(), // Store as string number, convert to language format during render
        mulant: existingRow.mulant || "",
        level1: existingRow.level1 || "",
        level2: existingRow.level2 || "",
        other: existingRow.other || "",
        total: existingRow.total || "",
      };
    });
  };

  const getFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          userFormMappingId: ufmId,
          isPtOne: false,
          year: 2025
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formData = Object.values(data?.teacherPt2Forms || {}).every(
          (value) => value === null
        )
          ? {}
          : data?.teacherPt2Forms || {};

        // List of fields to exclude (file input fields)
        const jsonStringFields = [
          "TEACHER_PT2_Q5",
          "TEACHER_PT2_Q9",
          "TEACHER_PT2_Q10",
          "TEACHER_PT2_Q11",
          "TEACHER_PT2_Q12",
          "TEACHER_PT2_Q13",
          "TEACHER_PT2_Q14",
          "TEACHER_PT2_Q15",
          "TEACHER_PT2_Q16",
        ];

        const fileFields = ["TEACHER_PT2_Q19"];

        const JSONwithYearFields = [
          "TEACHER_PT2_Q9",
          "TEACHER_PT2_Q10",
          "TEACHER_PT2_Q11",
          "TEACHER_PT2_Q12",
          "TEACHER_PT2_Q13",
          "TEACHER_PT2_Q14",
          "TEACHER_PT2_Q15",
        ];

        const yearFields = ["TEACHER_PT2_Q4_B"];

        const formattedData = Object.keys(formData).reduce(
          (acc: { [key: string]: any }, key) => {
            if (jsonStringFields.includes(key)) {
              try {
                // Special handling for Q5
                if (key === "TEACHER_PT2_Q5") {
                  const parsedArr = formData[key]
                    ? JSON.parse(formData[key])
                    : [];

                  // Check if we have valid Q5 data or if it's corrupted/invalid
                  if (
                    !parsedArr ||
                    parsedArr.length === 0 ||
                    parsedArr.some(
                      (item: any) => !item.year || item.year.includes("Invalid")
                    )
                  ) {
                    // Generate fresh Q5 data with last 5 years
                    acc[key] = Array.from({ length: 5 }, (_, index) => ({
                      key: index,
                      year: getNumberInLanguage(
                        Number(new Date().getFullYear() - index)
                      ),
                      mulant: "",
                      level1: "",
                      level2: "",
                      other: "",
                      total: "",
                    }));
                  } else {
                    // Use existing data but ensure year is properly formatted
                    acc[key] = parsedArr.map((item: any, index: number) => ({
                      ...item,
                      key: index,
                      year:
                        item.year ||
                        getNumberInLanguage(
                          Number(new Date().getFullYear() - index)
                        ),
                    }));
                  }
                } else {
                  // Handle other JSON fields
                  const parsedArr = formData[key]
                    ? JSON.parse(formData[key])
                    : [{}];

                  if (JSONwithYearFields.includes(key)) {
                    parsedArr.forEach((item: any) => {
                      item.year = dayjs(
                        item.year?.toString(),
                        "YYYY"
                      )?.isValid()
                        ? dayjs(item.year?.toString(), "YYYY")
                        : item.year;
                    });
                  }
                  acc[key] = parsedArr;
                }
              } catch (error) {
                console.error(`Error parsing ${key}:`, error);
                // Fallback for Q5 if JSON parsing fails
                if (key === "TEACHER_PT2_Q5") {
                  acc[key] = Array.from({ length: 5 }, (_, index) => ({
                    key: index,
                    year: getNumberInLanguage(
                      Number(new Date().getFullYear() - index)
                    ),
                    mulant: "",
                    level1: "",
                    level2: "",
                    other: "",
                    total: "",
                  }));
                } else {
                  acc[key] = [{}];
                }
              }
            } else if (fileFields.includes(key)) {
              acc[key] = "";
            } else if (yearFields.includes(key)) {
              acc[key] = dayjs(formData[key], "YYYY")?.isValid()
                ? dayjs(formData[key], "YYYY")
                : formData[key];
            } else {
              acc[key] = formData[key];
            }
            return acc;
          },
          {}
        );

        if (formattedData) {
          form.setFieldsValue(formattedData);

          // Ensure Q5 data is always properly formatted after loading from backend
          const currentQ5Data = formattedData.TEACHER_PT2_Q5 || [];
          const correctedQ5Data = generateCorrectQ5Data(currentQ5Data);

          // Update form with corrected Q5 data
          form.setFieldValue("TEACHER_PT2_Q5", correctedQ5Data);

          // Save corrected Q5 data to backend (without triggering submission)
          await handleFieldChange(
            "TEACHER_PT2_Q5",
            JSON.stringify(correctedQ5Data)
          );
        }

        setDocumentsData(data?.documents);

        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneratePDF = (id: string, fileName: string) => {
    setApplyPdfStyles(true);
    setTimeout(() => {
      Utils.generatePDF(id, fileName);
      setApplyPdfStyles(false);
    }, 0);
  };

  useEffect(() => {
    if (searchParams) {
      const formId = EncryptUtils.decrypt(searchParams.get("id2") || "");
      const ufmId = EncryptUtils.decrypt(searchParams.get("ufmid") || "");
      const formFor = EncryptUtils.decrypt(searchParams.get("ff") || "");
      setFormId(formId);
      setUfmId(ufmId);
      setFormFor(formFor);
    }
    if (currentFormId && currentUfmId) {
      setFormId(EncryptUtils.decrypt(currentFormId));
      setUfmId(EncryptUtils.decrypt(currentUfmId));
    }
  }, [searchParams, currentFormId, currentUfmId]);

  // Utility function to calculate the total for a row
  const calculateTotal = (row: any, columns: string[]) => {
    return columns.reduce((acc, column) => {
      return acc + parseInt(row[column] || 0);
    }, 0);
  };

  // Reusable function to update table data
  const updateTableData = (
    key: string, // Form field key (e.g., "TEACHER_PT2_Q5")
    index: number, // Row index
    field: string, // Field being updated (e.g., "mulant")
    value: string, // New value for the field
    columns: string[], // Columns to include in the total calculation
    form: any, // Form instance
    handleFieldChange: (field: string, value: any) => void // Function to persist changes
  ) => {
    // Get the current table data from the form
    const tableData = form.getFieldValue(key) || [];

    // Update the specific row and field
    const updatedRow = {
      ...tableData[index],
      [field]: value,
    };

    // Calculate the total for the row
    updatedRow.total = calculateTotal(updatedRow, columns).toString();

    // Update the table data
    const updatedTableData = [...tableData];
    updatedTableData[index] = updatedRow;

    // Update the form field with the new data
    form.setFieldsValue({ [key]: updatedTableData });

    // Persist the changes by calling handleFieldChange
    // Use different serialization for Q5 vs other questions
    if (key === "TEACHER_PT2_Q5") {
      // Q5 doesn't have date fields that need special handling
      handleFieldChange(key, JSON.stringify(updatedTableData));
    } else {
      // Other questions may have year fields that need proper serialization
      handleFieldChange(key, serializeTableData(updatedTableData));
    }
  };

  const PREVIEW = Text({ tid: "PREVIEW", def: "Preview" });
  const DROPDOWN_PLACEHOLDER = Text({
    tid: "DROPDOWN_PLACEHOLDER",
    def: "DROPDOWN_PLACEHOLDER",
  });

  const handleDateChange = (
    form: any,
    fieldName: string,
    index: number,
    dateString: string,
    handleFieldChange: (field: string, value: any) => void
  ) => {
    if (typeof dateString === "string" && dateString.length === 4) {
      handleFieldChange(
        fieldName,
        JSON.stringify(
          form.getFieldValue(fieldName).map((item: any, i: number) =>
            i === index
              ? {
                  ...item,
                  year: dateString,
                }
              : {
                  ...item,
                  year:
                    typeof item.year === "string" && item.year.length === 4
                      ? item.year // Keep existing year as string if it's already in YYYY format
                      : (dayjs(item.year, "YYYY") as any)["$y"], // Only convert if it's not already a YYYY string
                }
          )
        )
      );
    }
  };

  // Helper function to properly serialize table data with year fields
  const serializeTableData = (tableData: any[]) => {
    return JSON.stringify(
      tableData.map((item: any) => {
        const serializedItem = { ...item };
        // Convert year field to string if it exists and is not already a string
        if (serializedItem.year) {
          if (typeof serializedItem.year === "string") {
            // Check if it's already in YYYY format
            if (/^\d{4}$/.test(serializedItem.year)) {
              // If it's already a string in YYYY format, keep it as is
              serializedItem.year = serializedItem.year;
            } else {
              // If it's a string but not in YYYY format, convert it
              serializedItem.year = dayjs(serializedItem.year)?.format("YYYY");
            }
          } else if (
            serializedItem.year._isAMomentObject ||
            serializedItem.year.$isDayjsObject
          ) {
            // If it's a dayjs/moment object, format it as YYYY
            serializedItem.year = dayjs(serializedItem.year).format("YYYY");
          } else if (serializedItem.year instanceof Date) {
            // If it's a Date object, format it as YYYY
            serializedItem.year = dayjs(serializedItem.year).format("YYYY");
          } else {
            // For any other type, try to convert it
            serializedItem.year = dayjs(serializedItem.year).format("YYYY");
          }
        }
        return serializedItem;
      })
    );
  };

  // Generic function to remove a row
  const removeRow = async (form: any, key: string, index: number) => {
    const currentData = form.getFieldValue(key) || [];
    const updatedData = currentData.filter((_: any, i: number) => i !== index);
    form.setFieldsValue({
      [key]: updatedData,
    });
    setRowCount((prev) => ({ ...prev, [key]: updatedData.length }));
    await handleFieldChange(key, serializeTableData(updatedData));

    // Update Q16 summary table if the removed row was from a table with documents
    const tablesWithDocuments = [
      "TEACHER_PT2_Q9",
      "TEACHER_PT2_Q10",
      "TEACHER_PT2_Q11",
      "TEACHER_PT2_Q12",
    ];
    if (tablesWithDocuments.includes(key)) {
      // Small delay to ensure form state is updated
      setTimeout(async () => {
        const uploadedDocuments = getUploadedDocuments();
        form.setFieldsValue({
          TEACHER_PT2_Q16: uploadedDocuments,
        });
        await handleFieldChange(
          "TEACHER_PT2_Q16",
          JSON.stringify(uploadedDocuments)
        );
      }, 100);
    }
  };

  // Initialize form values (if needed)
  useEffect(() => {
    form.setFieldsValue({
      TEACHER_PT2_Q5: generateCorrectQ5Data(),
      TEACHER_PT2_Q9: [
        {
          Conference_or_Meeting: "",
          Organizer: "",
          year: "",
          TEACHER_PT2_Q9_DOC: "",
        },
      ],
      TEACHER_PT2_Q10: [
        {
          yagyaWork: "",
          place: "",
          year: "",
          TEACHER_PT2_Q10_DOC: "",
        },
      ],
      TEACHER_PT2_Q11: [
        {
          awardName: "",
          Presenter: "",
          year: "",
          TEACHER_PT2_Q11_DOC: "",
        },
      ],
      TEACHER_PT2_Q12: [
        {
          title: "",
          publisher: "",
          isbn: "",
          year: "",
          TEACHER_PT2_Q12_DOC: "",
        },
      ],
      TEACHER_PT2_Q13: [
        {
          vedicStudent: "",
          levelOfStudy: "",
          examinationInstitution: "",
          year: "",
        },
      ],
      TEACHER_PT2_Q14: [
        {
          nameOfVedicStudent: "",
          age: "",
          year: "",
          currentLevelOfEducation: "",
        },
      ],
      TEACHER_PT2_Q15: [
        {
          nameOfVedicStudent: "",
          year: "",
          currentlyTeachingVedaInstitution: "",
        },
      ],
      TEACHER_PT2_Q16: [
        {
          documentDetails: "",
        },
      ],
    });
  }, []);

  // Q9 -----------------------------------------Q5-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  // Columns and data source for question 5
  const TABLE_COLUMNS_TEACHER_PT2_Q5 = [
    {
      title: Text({ tid: "TEACHER_PT2_Q5_A", def: "TEACHER_PT2_Q5_A" }),
      dataIndex: "year",
      key: "year",
      render: (year: string) => getNumberInLanguage(Number(year)),
    },
    {
      title: Text({ tid: "TEACHER_PT2_Q5_B", def: "TEACHER_PT2_Q5_B" }),
      dataIndex: "mulant",
      key: "mulant",
      render: (text: any, record: any, index: number) => (
        <Form.Item
          name={["TEACHER_PT2_Q5", index, "mulant"]}
          rules={numberInputRules({
            required: true,
            allowNegative: false,
            min: 0,
          })}
        >
          <Input
            type='number'
            disabled={disabled}
            onChange={(e) =>
              updateTableData(
                "TEACHER_PT2_Q5",
                index,
                "mulant",
                e.target.value,
                ["mulant", "level1", "level2", "other"],
                form,
                handleFieldChange
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "TEACHER_PT2_Q5_C", def: "TEACHER_PT2_Q5_C" }),
      dataIndex: "level1",
      key: "level1",
      render: (text: any, record: any, index: number) => (
        <Form.Item
          name={["TEACHER_PT2_Q5", index, "level1"]}
          rules={numberInputRules({
            required: true,
            allowNegative: false,
            min: 0,
          })}
        >
          <Input
            type='number'
            disabled={disabled}
            onChange={(e) =>
              updateTableData(
                "TEACHER_PT2_Q5",
                index,
                "level1",
                e.target.value,
                ["mulant", "level1", "level2", "other"],
                form,
                handleFieldChange
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "TEACHER_PT2_Q5_D", def: "TEACHER_PT2_Q5_D" }),
      dataIndex: "level2",
      key: "level2",
      render: (text: any, record: any, index: number) => (
        <Form.Item
          name={["TEACHER_PT2_Q5", index, "level2"]}
          rules={numberInputRules({
            required: true,
            allowNegative: false,
            min: 0,
          })}
        >
          <Input
            type='number'
            disabled={disabled}
            onChange={(e) =>
              updateTableData(
                "TEACHER_PT2_Q5",
                index,
                "level2",
                e.target.value,
                ["mulant", "level1", "level2", "other"],
                form,
                handleFieldChange
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "TEACHER_PT2_Q5_E", def: "TEACHER_PT2_Q5_E" }),
      dataIndex: "other",
      key: "other",
      render: (text: any, record: any, index: number) => (
        <Form.Item
          name={["TEACHER_PT2_Q5", index, "other"]}
          rules={numberInputRules({
            required: true,
            allowNegative: false,
            min: 0,
          })}
        >
          <Input
            type='number'
            disabled={disabled}
            onChange={(e) =>
              updateTableData(
                "TEACHER_PT2_Q5",
                index,
                "other",
                e.target.value,
                ["mulant", "level1", "level2", "other"],
                form,
                handleFieldChange
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "TEACHER_PT2_Q5_F", def: "TEACHER_PT2_Q5_F" }),
      dataIndex: "total",
      key: "total",
      render: (text: any, record: any, index: number) => (
        <Form.Item name={["TEACHER_PT2_Q5", index, "total"]}>
          <Input
            type='number'
            disabled={true}
            value={record.total}
          />
        </Form.Item>
      ),
    },
  ];

  // Q9 -----------------------------------------Q9-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    form: any,
    index: number,
    name: string,
    fieldName: string,
    fileFieldName: string,
    handleFieldChange: (field: string, value: any) => void,
    uploadDocument: (
      file: File,
      type: eDocType,
      documentName?: string
    ) => Promise<string>
  ) => {
    if (e.target.files && e.target.files[0]) {
      // Get current row data
      const currentData = form.getFieldValue(fieldName) || [];
      const currentRow = currentData[index] || {};

      // Validate that the name/title field is filled before upload
      let nameField = "";
      let fieldLabel = "";

      switch (fieldName) {
        case "TEACHER_PT2_Q9":
          nameField = currentRow.Conference_or_Meeting;
          fieldLabel = "Conference or Meeting name";
          break;
        case "TEACHER_PT2_Q10":
          nameField = currentRow.yagyaWork;
          fieldLabel = "Yagya Work";
          break;
        case "TEACHER_PT2_Q11":
          nameField = currentRow.awardName;
          fieldLabel = "Award Name";
          break;
        case "TEACHER_PT2_Q12":
          nameField = currentRow.title;
          fieldLabel = "Title";
          break;
        default:
          break;
      }

      if (!nameField || nameField.trim() === "") {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: `Please enter the title before uploading a document.`,
        });
        // Reset the file input
        e.target.value = "";
        return;
      }

      const docId = await uploadDocument(
        e.target.files[0],
        eDocType.OTHER,
        `${fieldName}_${index + 1}`
      );

      form.setFieldValue(
        [fieldName, parseInt(name), `${fieldName}_DOC`],
        docId
      );

      const updatedJson = JSON.parse(
        JSON.stringify(form.getFieldValue(fieldName))
      ).map((item: any) => ({
        ...item,
        [fileFieldName]: "",
      }));

      try {
        await handleFieldChange(fieldName, serializeTableData(updatedJson));
        await getFormData();

        // Update Q16 with new document list and save it
        const uploadedDocuments = getUploadedDocuments();
        form.setFieldsValue({
          TEACHER_PT2_Q16: uploadedDocuments,
        });
        await handleFieldChange(
          "TEACHER_PT2_Q16",
          JSON.stringify(uploadedDocuments)
        );

        onShowToast({
          type: ToastType.success,
          title: <FaCheck />,
          position: ToastOpen.leftBottom,
          content: "File uploaded successfully!",
        });
      } catch (error) {
        console.error("Error during file upload or data refresh:", error);

        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: "Failed to upload file. Please try again.",
        });
      }
    }
  };

  // Columns for Question 9
  const TABLE_COLUMNS_TEACHER_PT2_Q9: TableProps<DATA_TYPE_TEACHER_PT2_Q9>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q9_A", def: "TEACHER_PT2_Q9_A" }),
        dataIndex: "Conference_or_Meeting",
        key: "Conference_or_Meeting",
        render: (_: any, record: any, index: number) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q9",
            index,
            "TEACHER_PT2_Q9_DOC",
          ]);
          return (
            <Form.Item
              className='ant-table-input'
              name={["TEACHER_PT2_Q9", index, "Conference_or_Meeting"]}
              rules={inputRequiredRule}
            >
              <Input
                disabled={disabled || !!docUrl}
                onBlur={(e) =>
                  handleFieldChange(
                    "TEACHER_PT2_Q9",
                    serializeTableData(form.getFieldValue("TEACHER_PT2_Q9"))
                  )
                }
              />
            </Form.Item>
          );
        },
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q9_B", def: "TEACHER_PT2_Q9_B" }),
        dataIndex: "Organizer",
        key: "Organizer",
        render: (_: any, record: any, index: number) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q9", index, "Organizer"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q9",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q9"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q9_C", def: "TEACHER_PT2_Q9_C" }),
        dataIndex: "year",
        key: "year",
        render: (_: any, record: any, index: number) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q9", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder=''
              disabled={disabled}
              disabledDate={disableFutureYears}
              onKeyDown={(e) => e.preventDefault()}
              picker='year'
              format='YYYY'
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "TEACHER_PT2_Q9",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "ATTACHED_DOCUMENT", def: "Document Upload" }),
        dataIndex: "documentUpload",
        key: "documentUpload",
        width: "20%",
        render: (_: any, record: any, index: number) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q9",
            index,
            "TEACHER_PT2_Q9_DOC",
          ]);
          return (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type='file'
                accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                disabled={disabled}
                onChange={(e) =>
                  handleFileUpload(
                    e,
                    form,
                    index,
                    index.toString(),
                    "TEACHER_PT2_Q9",
                    "documentUpload",
                    handleFieldChange,
                    uploadDocument
                  )
                }
                style={{ fontSize: "12px", cursor: "pointer" }}
              />
              {docUrl && (
                <a
                  href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docUrl}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='primary-link'
                >
                  {Text({ tid: "PREVIEW", def: "Preview" })}
                </a>
              )}
            </div>
          );
        },
      },
      {
        title: Text({ tid: "ACTION", def: "ACTION" }),
        dataIndex: "action",
        width: "100px",

        render: (_: any, record: any, index: number) => (
          <Form.Item className='ant-table-input'>
            <Button
              disabled={disabled}
              variant='filled'
              onClick={() => removeRow(form, "TEACHER_PT2_Q9", index)}
              className='actionButton'
            >
              {Text({ tid: "REMOVE", def: "remove" })}
            </Button>
          </Form.Item>
        ),
      },
    ];

  // Q10 -----------------------------------------Q10-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_TEACHER_PT2_Q10: TableProps<DATA_TYPE_TEACHER_PT2_Q10>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q10_A", def: "TEACHER_PT2_Q10_A" }),
        dataIndex: "yagyaWork",
        key: "yagyaWork",
        render: (_, record, index) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q10",
            index,
            "TEACHER_PT2_Q10_DOC",
          ]);
          return (
            <Form.Item
              className='ant-table-input'
              name={["TEACHER_PT2_Q10", index, "yagyaWork"]}
              rules={inputRequiredRule}
            >
              <Input
                disabled={disabled || !!docUrl}
                onBlur={(e) =>
                  handleFieldChange(
                    "TEACHER_PT2_Q10",
                    serializeTableData(form.getFieldValue("TEACHER_PT2_Q10"))
                  )
                }
              />
            </Form.Item>
          );
        },
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q10_B", def: "TEACHER_PT2_Q10_B" }),
        dataIndex: "place",
        key: "place",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q10", index, "place"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q10",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q10"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q10_C", def: "TEACHER_PT2_Q10_C" }),
        dataIndex: "year",
        key: "year",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q10", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder=''
              disabled={disabled}
              disabledDate={disableFutureYears}
              onKeyDown={(e) => e.preventDefault()}
              picker='year' // This ensures only years can be selected
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "TEACHER_PT2_Q10",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "ATTACHED_DOCUMENT", def: "Document Upload" }),
        dataIndex: "documentUpload",
        key: "documentUpload",
        width: "20%",
        render: (_: any, record: any, index: number) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q10",
            index,
            "TEACHER_PT2_Q10_DOC",
          ]);
          return (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type='file'
                accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                disabled={disabled}
                onChange={(e) =>
                  handleFileUpload(
                    e,
                    form,
                    index,
                    index.toString(),
                    "TEACHER_PT2_Q10",
                    "documentUpload",
                    handleFieldChange,
                    uploadDocument
                  )
                }
                style={{ fontSize: "12px" }}
              />
              {docUrl && (
                <a
                  href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docUrl}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='primary-link'
                >
                  {Text({ tid: "PREVIEW", def: "Preview" })}
                </a>
              )}
            </div>
          );
        },
      },
      {
        title: Text({ tid: "ACTION", def: "ACTION" }),
        dataIndex: "action",
        width: "100px",

        key: "action",
        render: (_, record, index) => (
          <Form.Item className='ant-table-input'>
            <Button
              disabled={disabled}
              variant='filled'
              onClick={() => removeRow(form, "TEACHER_PT2_Q10", index)}
              className='actionButton'
            >
              {Text({ tid: "REMOVE", def: "remove" })}
            </Button>
          </Form.Item>
        ),
      },
    ];

  // Q11 -----------------------------------------Q11-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_TEACHER_PT2_Q11: TableProps<DATA_TYPE_TEACHER_PT2_Q11>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q11_A", def: "TEACHER_PT2_Q11_A" }),
        dataIndex: "awardName",
        key: "awardName",
        render: (_, record, index) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q11",
            index,
            "TEACHER_PT2_Q11_DOC",
          ]);
          return (
            <Form.Item
              className='ant-table-input'
              name={["TEACHER_PT2_Q11", index, "awardName"]}
              rules={inputRequiredRule}
            >
              <Input
                disabled={disabled || !!docUrl}
                onBlur={(e) =>
                  handleFieldChange(
                    "TEACHER_PT2_Q11",
                    serializeTableData(form.getFieldValue("TEACHER_PT2_Q11"))
                  )
                }
              />
            </Form.Item>
          );
        },
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q11_B", def: "TEACHER_PT2_Q11_B" }),
        dataIndex: "Presenter",
        key: "Presenter",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q11", index, "Presenter"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q11",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q11"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q11_C", def: "TEACHER_PT2_Q11_C" }),
        dataIndex: "year",
        key: "year",
        width: "200px",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q11", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder=''
              disabled={disabled}
              disabledDate={disableFutureYears}
              picker='year'
              format='YYYY'
              onKeyDown={(e) => e.preventDefault()}
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "TEACHER_PT2_Q11",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "ATTACHED_DOCUMENT", def: "Document Upload" }) + "*",
        dataIndex: "documentUpload",
        key: "documentUpload",
        width: "20%",
        render: (_: any, record: any, index: number) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q11",
            index,
            "TEACHER_PT2_Q11_DOC",
          ]);
          return (
            <div>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                {/* File input without Form.Item wrapper */}
                <input
                  type='file'
                  accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                  disabled={disabled}
                  onChange={(e) =>
                    handleFileUpload(
                      e,
                      form,
                      index,
                      index.toString(),
                      "TEACHER_PT2_Q11",
                      "documentUpload",
                      handleFieldChange,
                      uploadDocument
                    )
                  }
                  style={{ fontSize: "12px" }}
                />
                {docUrl && (
                  <a
                    href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docUrl}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='primary-link'
                  >
                    {Text({ tid: "PREVIEW", def: "Preview" })}
                  </a>
                )}
              </div>
              {/* Custom validation message */}
              {!docUrl && (
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginTop: "4px",
                    lineHeight: "1.5715",
                  }}
                >
                  {Text({ tid: "REQUIRED", def: "Required" })}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: Text({ tid: "ACTION", def: "ACTION" }),
        dataIndex: "action",
        width: "100px",
        key: "action",
        render: (_, record, index) => (
          <Form.Item className='ant-table-input'>
            <Button
              disabled={disabled}
              variant='filled'
              onClick={() => removeRow(form, "TEACHER_PT2_Q11", index)}
              className='actionButton'
            >
              {Text({ tid: "REMOVE", def: "remove" })}
            </Button>
          </Form.Item>
        ),
      },
    ];

  // Q12 -----------------------------------------Q12-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_TEACHER_PT2_Q12: TableProps<DATA_TYPE_TEACHER_PT2_Q12>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q12_A", def: "TEACHER_PT2_Q12_A" }),
        dataIndex: "title",
        key: "title",
        render: (_, record, index) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q12",
            index,
            "TEACHER_PT2_Q12_DOC",
          ]);
          return (
            <Form.Item
              className='ant-table-input'
              name={["TEACHER_PT2_Q12", index, "title"]}
              rules={inputRequiredRule}
            >
              <Input
                disabled={disabled || !!docUrl}
                onBlur={(e) =>
                  handleFieldChange(
                    "TEACHER_PT2_Q12",
                    serializeTableData(form.getFieldValue("TEACHER_PT2_Q12"))
                  )
                }
              />
            </Form.Item>
          );
        },
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q12_B", def: "TEACHER_PT2_Q12_B" }),
        dataIndex: "publisher",
        key: "publisher",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q12", index, "publisher"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q12",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q12"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q12_C", def: "TEACHER_PT2_Q12_C" }),
        dataIndex: "isbn",
        key: "isbn",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q12", index, "isbn"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q12",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q12"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q12_D", def: "TEACHER_PT2_Q12_D" }),
        dataIndex: "year",
        key: "year",
        width: "200px",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q12", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder=''
              disabled={disabled}
              disabledDate={disableFutureYears}
              picker='year'
              onKeyDown={(e) => e.preventDefault()}
              format='YYYY'
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "TEACHER_PT2_Q12",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "ATTACHED_DOCUMENT", def: "Document Upload" }),
        dataIndex: "documentUpload",
        key: "documentUpload",
        width: "20%",
        render: (_: any, record: any, index: number) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q12",
            index,
            "TEACHER_PT2_Q12_DOC",
          ]);
          return (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type='file'
                accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                disabled={disabled}
                onChange={(e) =>
                  handleFileUpload(
                    e,
                    form,
                    index,
                    index.toString(),
                    "TEACHER_PT2_Q12",
                    "documentUpload",
                    handleFieldChange,
                    uploadDocument
                  )
                }
                style={{ fontSize: "12px" }}
              />
              {docUrl && (
                <a
                  href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docUrl}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='primary-link'
                >
                  {Text({ tid: "PREVIEW", def: "Preview" })}
                </a>
              )}
            </div>
          );
        },
      },
      {
        title: Text({ tid: "ACTION", def: "ACTION" }),
        dataIndex: "action",
        key: "action",
        width: "100px",
        render: (_, record, index) => (
          <Form.Item className='ant-table-input'>
            <Button
              disabled={disabled}
              variant='filled'
              onClick={() => removeRow(form, "TEACHER_PT2_Q12", index)}
              className='actionButton'
            >
              {Text({ tid: "REMOVE", def: "remove" })}
            </Button>
          </Form.Item>
        ),
      },
    ];

  // --------------------------------------------------------------------------------
  // Q13 -----------------------------------------Q13-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_TEACHER_PT2_Q13: TableProps<DATA_TYPE_TEACHER_PT2_Q13>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q13_B", def: "Name of Ved student" }),
        dataIndex: "vedicStudent",
        key: "vedicStudent",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q13", index, "vedicStudent"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q13",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q13"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q13_C", def: "Level of study" }),
        dataIndex: "levelOfStudy",
        key: "levelOfStudy",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q13", index, "levelOfStudy"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q13",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q13"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "TEACHER_PT2_Q13_D",
          def: "Name of the examination institution",
        }),
        dataIndex: "examinationInstitution",
        key: "examinationInstitution",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q13", index, "examinationInstitution"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q13",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q13"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q13_E", def: "Year" }),
        dataIndex: "year",
        key: "year",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q13", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder=''
              disabled={disabled}
              disabledDate={disableFutureYears}
              onKeyDown={(e) => e.preventDefault()}
              picker='year' // This ensures only years can be selected
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "TEACHER_PT2_Q13",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange
                )
              }
            />
          </Form.Item>
        ),
        width: "200px",
      },
      {
        title: Text({ tid: "ACTION", def: "ACTION" }),
        dataIndex: "action",
        key: "action",
        width: "100px",
        render: (_, record, index) => (
          <Form.Item className='ant-table-input'>
            <Button
              disabled={disabled}
              variant='filled'
              onClick={() => removeRow(form, "TEACHER_PT2_Q13", index)}
              className='actionButton'
            >
              {Text({ tid: "REMOVE", def: "remove" })}
            </Button>
          </Form.Item>
        ),
      },
    ];

  // Q14 -----------------------------------------Q14-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_TEACHER_PT2_Q14: TableProps<DATA_TYPE_TEACHER_PT2_Q14>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q14_A", def: "Name of Ved student" }),
        dataIndex: "nameOfVedicStudent",
        key: "nameOfVedicStudent",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q14", index, "nameOfVedicStudent"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q14",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q14"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q14_B", def: "Age" }),
        dataIndex: "age",
        key: "age",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q14", index, "age"]}
            rules={numberInputRules({
              required: true,
              allowNegative: false,
              min: 0,
              max: 120,
            })}
          >
            <Input
              type='number'
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q14",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q14"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "TEACHER_PT2_Q14_C",
          def: "Year of commencement of education",
        }),
        dataIndex: "year",
        key: "year",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q14", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder=''
              disabled={disabled}
              disabledDate={disableFutureYears}
              picker='year'
              onKeyDown={(e) => e.preventDefault()}
              format='YYYY'
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "TEACHER_PT2_Q14",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "TEACHER_PT2_Q14_D",
          def: "Current level of education",
        }),
        dataIndex: "currentLevelOfEducation",
        key: "currentLevelOfEducation",
        width: "200px",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q14", index, "currentLevelOfEducation"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q14",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q14"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "ACTION", def: "ACTION" }),
        dataIndex: "action",
        key: "action",
        width: "100px",
        render: (_, record, index) => (
          <Form.Item className='ant-table-input'>
            <Button
              disabled={disabled}
              variant='filled'
              onClick={() => removeRow(form, "TEACHER_PT2_Q14", index)}
              className='actionButton'
            >
              {Text({ tid: "REMOVE", def: "remove" })}
            </Button>
          </Form.Item>
        ),
      },
    ];

  // Q15 -----------------------------------------Q15-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_TEACHER_PT2_Q15: TableProps<DATA_TYPE_TEACHER_PT2_Q15>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q15_A", def: "Name of Ved student" }),
        dataIndex: "nameOfVedicStudent",
        key: "nameOfVedicStudent",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q15", index, "nameOfVedicStudent"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q15",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q15"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "TEACHER_PT2_Q15_B",
          def: "Year of completion of Ved studies",
        }),
        dataIndex: "year",
        key: "year",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={["TEACHER_PT2_Q15", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder=''
              disabled={disabled}
              disabledDate={disableFutureYears}
              picker='year'
              format='YYYY'
              onKeyDown={(e) => e.preventDefault()}
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "TEACHER_PT2_Q15",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({
          tid: "TEACHER_PT2_Q15_C",
          def: "Institution where they are currently teaching Vedas",
        }),
        dataIndex: "currentlyTeachingVedaInstitution",
        key: "currentlyTeachingVedaInstitution",
        render: (_, record, index) => (
          <Form.Item
            className='ant-table-input'
            name={[
              "TEACHER_PT2_Q15",
              index,
              "currentlyTeachingVedaInstitution",
            ]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled}
              onBlur={(e) =>
                handleFieldChange(
                  "TEACHER_PT2_Q15",
                  serializeTableData(form.getFieldValue("TEACHER_PT2_Q15"))
                )
              }
            />
          </Form.Item>
        ),
      },
      {
        title: Text({ tid: "ACTION", def: "ACTION" }),
        dataIndex: "action",
        key: "action",
        width: "100px",
        render: (_, record, index) => (
          <Form.Item className='ant-table-input'>
            <Button
              disabled={disabled}
              variant='filled'
              onClick={() => removeRow(form, "TEACHER_PT2_Q15", index)}
              className='actionButton'
            >
              {Text({ tid: "REMOVE", def: "remove" })}
            </Button>
          </Form.Item>
        ),
      },
    ];

  // Q16 -----------------------------------------Q16-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  // Function to collect all uploaded document titles
  const getUploadedDocuments = () => {
    const documents: { documentDetails: string; documentUrl?: string }[] = [];

    // Collect from Q9
    const q9Data = form.getFieldValue("TEACHER_PT2_Q9") || [];
    q9Data.forEach((item: any, index: number) => {
      const docUrl = form.getFieldValue([
        "TEACHER_PT2_Q9",
        index,
        "TEACHER_PT2_Q9_DOC",
      ]);
      if (docUrl && item.Conference_or_Meeting) {
        documents.push({
          documentDetails: `${item.Conference_or_Meeting}`,
          documentUrl: docUrl,
        });
      }
    });

    // Collect from Q10
    const q10Data = form.getFieldValue("TEACHER_PT2_Q10") || [];
    q10Data.forEach((item: any, index: number) => {
      const docUrl = form.getFieldValue([
        "TEACHER_PT2_Q10",
        index,
        "TEACHER_PT2_Q10_DOC",
      ]);
      if (docUrl && item.yagyaWork) {
        documents.push({
          documentDetails: `${item.yagyaWork}`,
          documentUrl: docUrl,
        });
      }
    });

    // Collect from Q11
    const q11Data = form.getFieldValue("TEACHER_PT2_Q11") || [];
    q11Data.forEach((item: any, index: number) => {
      const docUrl = form.getFieldValue([
        "TEACHER_PT2_Q11",
        index,
        "TEACHER_PT2_Q11_DOC",
      ]);
      if (docUrl && item.awardName) {
        documents.push({
          documentDetails: `${item.awardName}`,
          documentUrl: docUrl,
        });
      }
    });

    // Collect from Q12
    const q12Data = form.getFieldValue("TEACHER_PT2_Q12") || [];
    q12Data.forEach((item: any, index: number) => {
      const docUrl = form.getFieldValue([
        "TEACHER_PT2_Q12",
        index,
        "TEACHER_PT2_Q12_DOC",
      ]);
      if (docUrl && item.title) {
        documents.push({
          documentDetails: `${item.title} `,
          documentUrl: docUrl,
        });
      }
    });

    return documents;
  };

  // Auto-update Q16 display when component loads (without backend save)
  useEffect(() => {
    const uploadedDocuments = getUploadedDocuments();
    form.setFieldsValue({
      TEACHER_PT2_Q16: uploadedDocuments,
    });
  }, [formId, ufmId]); // Only run when form data is loaded

  const TABLE_COLUMNS_TEACHER_PT2_Q16: TableProps<DATA_TYPE_TEACHER_PT2_Q16>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "TEACHER_PT2_Q16_B", def: "Document details" }),
        dataIndex: "documentDetails",
        key: "documentDetails",
        render: (text: string) => <span style={{ color: "#666" }}>{text}</span>,
      },
      {
        title: Text({ tid: "PREVIEW", def: "Preview" }),
        dataIndex: "preview",
        key: "preview",
        width: "120px",
        render: (_, record: any) => {
          const documentUrl = record.documentUrl;
          return documentUrl ? (
            (() => {
              const fileUrl = `${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${documentUrl}`;
              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(documentUrl);

              return isImage ? (
                <Image
                  width={50}
                  height={50}
                  src={fileUrl}
                  preview={{
                    src: fileUrl,
                  }}
                  style={{ objectFit: "cover", cursor: "pointer" }}
                />
              ) : (
                <a
                  href={fileUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='primary-link'
                >
                  {Text({ tid: "PREVIEW", def: "Preview" })}
                </a>
              );
            })()
          ) : (
            <span style={{ color: "#ccc" }}>No document</span>
          );
        },
      },
    ];

  // Handle form submission with proper Q5 data formatting
  const handleFormSubmit = async () => {
    try {
      // Ensure Q5 data is properly formatted with correct years before submission
      const currentQ5Data = form.getFieldValue("TEACHER_PT2_Q5") || [];

      // Generate properly formatted Q5 data using helper function
      const formattedQ5Data = generateCorrectQ5Data(currentQ5Data);

      // Update form with properly formatted Q5 data
      form.setFieldValue("TEACHER_PT2_Q5", formattedQ5Data);

      // Prepare Q5 data for backend submission
      const q5ForSubmission = JSON.stringify(formattedQ5Data);

      // Create bulk payload with corrected Q5 data
      const bulkPayload = {
        TEACHER_PT2_Q5: q5ForSubmission,
      };

      // Submit form with corrected Q5 data
      await handleFieldChange("", "", bulkPayload, true);
    } catch (error) {
      console.error("Error during form submission:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Failed to submit form. Please try again.",
      });
    }
  };

  // ----------------------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    if (formId?.length && ufmId?.length) {
      getFormData();
    }
  }, [formId, ufmId]);

  return (
    <div
      id='teacherFormPart2'
      className={`${styles.formWrapper} studentPartTwo studentTeacherSchoolFormWrapper`}
    >
      <Form
        {...formItemLayout}
        form={form}
        name='register'
        onFinish={handleFormSubmit}
        initialValues={{}}
        scrollToFirstError
        layout='vertical'
      >
        {/* MAIN HEADING  */}
        <Row>
          <Flex
            gap='middle'
            align='start'
            justify='space-between'
            style={{ width: "100%" }}
          >
            <Col>
              <Typography.Title level={3}>
                {Text({
                  tid: "TEACHER_PART_2_FORM_HEADING",
                  def: "TEACHER_PART_2_FORM_HEADING",
                })}
              </Typography.Title>
              {!disabled && (
                <Typography.Text type='danger'>
                  {Text({
                    tid: "all_fields_are_mandatory",
                    def: "All fields are mandatory!",
                  })}
                </Typography.Text>
              )}
            </Col>
          </Flex>
        </Row>
        {/* 1. NAME */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_PT2_Q1'
              label={Text({
                tid: "TEACHER_PT2_Q1",
                def: "Name",
              })}
              rules={inputRequiredRule}
            >
              <Input
                disabled={disabled}
                onBlur={(e) => {
                  handleFieldChange("TEACHER_PT2_Q1", e.target.value);
                  handleFieldChange("TEACHER_PT2_Q17", e.target.value);
                  handleFieldChange("TEACHER_PT2_Q20", e.target.value);
                  form.setFieldValue("TEACHER_PT2_Q17", e.target.value);
                  form.setFieldValue("TEACHER_PT2_Q20", e.target.value);
                }}
                onChange={(e) => {
                  handleFieldChange("TEACHER_PT2_Q17", e.target.value);
                  handleFieldChange("TEACHER_PT2_Q20", e.target.value);
                  form.setFieldValue("TEACHER_PT2_Q17", e.target.value);
                  form.setFieldValue("TEACHER_PT2_Q20", e.target.value);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 2. Aadhaar Card Number */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_PT2_Q2'
              label={Text({
                tid: "TEACHER_PT2_Q2",
                def: "Aadhaar No.",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: /^[2-9]{1}[0-9]{11}$/, // Aadhaar must be 12 digits and should not start with 0 or 1
                  message: Text({
                    tid: "aadhaar_card_validation",
                    def: "Aadhaar must be 12 digits and should not start with 0 or 1",
                  }),
                },
              ]}
            >
              <Input
                disabled={disabled}
                maxLength={12}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
                onBlur={(e) =>
                  handleFieldChange("TEACHER_PT2_Q2", e.target.value)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 3. SPECIAL_FOR_KUMARADHYAPAKS */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q3",
                def: "Special for Kumaradhyapaks: If you are a Kumaradhyapak or you are imparting Ved education in your home, then what was the number of Ved students in your Gurukul in the last five years?",
              })}
            </Typography.Text>
          </Col>
        </Row>
        <div className='QuestionsSubSection'>
          {/* 3.1). TEACHER_PT2_Q3_A */}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_PT2_Q3_A'
                label={Text({
                  tid: "TEACHER_PT2_Q3_A",
                  def: "Name",
                })}
                rules={inputRequiredRule}
              >
                <Input
                  disabled={disabled}
                  onKeyDown={allowOnlyNumericInput_onKeyDown}
                  onBlur={(e) =>
                    handleFieldChange("TEACHER_PT2_Q3_A", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 3.2). TEACHER_PT2_Q3_B*/}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_PT2_Q3_B'
                label={Text({
                  tid: "TEACHER_PT2_Q3_B",
                  def: "Name",
                })}
                rules={inputRequiredRule}
              >
                <Input
                  onKeyDown={allowOnlyNumericInput_onKeyDown}
                  disabled={disabled}
                  onBlur={(e) =>
                    handleFieldChange("TEACHER_PT2_Q3_B", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 3.3). TEACHER_PT2_Q3_C*/}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_PT2_Q3_C'
                label={Text({
                  tid: "TEACHER_PT2_Q3_C",
                  def: "Name",
                })}
                rules={inputRequiredRule}
              >
                <Input
                  onKeyDown={allowOnlyNumericInput_onKeyDown}
                  disabled={disabled}
                  onBlur={(e) =>
                    handleFieldChange("TEACHER_PT2_Q3_C", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* 4. SPECIAL_FOR_KUMARADHYAPAKS */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q4",
                def: "Please provide details of all the education you have earned in the following subjects of the studied Vedas (see Ved Vidya Samakshata list):",
              })}
            </Typography.Text>
          </Col>
        </Row>
        <div className='QuestionsSubSection'>
          {/* 4.1). Select Subject */}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_PT2_Q4_A'
                label={Text({
                  tid: "TEACHER_PT2_Q4_A",
                  def: "TEACHER_PT2_Q4_A",
                })}
                rules={inputRequiredRule}
              >
                <Select
                  disabled={disabled}
                  onChange={(value) => {
                    handleFieldChange("TEACHER_PT2_Q4_A", value);
                  }}
                  placeholder={DROPDOWN_PLACEHOLDER}
                >
                  <Select.Option
                    value={Text({
                      tid: "SUBJECT_OPTIONS_BRAHMANA",
                      def: "SUBJECT_OPTIONS_BRAHMANA",
                    })}
                  >
                    {Text({
                      tid: "SUBJECT_OPTIONS_BRAHMANA",
                      def: "SUBJECT_OPTIONS_BRAHMANA",
                    })}
                  </Select.Option>
                  <Select.Option
                    value={Text({
                      tid: "SUBJECT_OPTIONS_ARANYAKA",
                      def: "SUBJECT_OPTIONS_ARANYAKA",
                    })}
                  >
                    {Text({
                      tid: "SUBJECT_OPTIONS_ARANYAKA",
                      def: "SUBJECT_OPTIONS_ARANYAKA",
                    })}
                  </Select.Option>
                  <Select.Option
                    value={Text({
                      tid: "SUBJECT_OPTIONS_UPANISHAD",
                      def: "SUBJECT_OPTIONS_UPANISHAD",
                    })}
                  >
                    {Text({
                      tid: "SUBJECT_OPTIONS_UPANISHAD",
                      def: "SUBJECT_OPTIONS_UPANISHAD",
                    })}
                  </Select.Option>
                  <Select.Option
                    value={Text({
                      tid: "SUBJECT_OPTIONS_SHADANGA",
                      def: "SUBJECT_OPTIONS_SHADANGA",
                    })}
                  >
                    {Text({
                      tid: "SUBJECT_OPTIONS_SHADANGA",
                      def: "SUBJECT_OPTIONS_SHADANGA",
                    })}
                  </Select.Option>
                  <Select.Option
                    value={Text({
                      tid: "SUBJECT_OPTIONS_LAKSHANA",
                      def: "SUBJECT_OPTIONS_LAKSHANA",
                    })}
                  >
                    {Text({
                      tid: "SUBJECT_OPTIONS_LAKSHANA",
                      def: "SUBJECT_OPTIONS_LAKSHANA",
                    })}
                  </Select.Option>
                  <Select.Option
                    value={Text({
                      tid: "SUBJECT_OPTIONS_BHASHYA",
                      def: "SUBJECT_OPTIONS_BHASHYA",
                    })}
                  >
                    {Text({
                      tid: "SUBJECT_OPTIONS_BHASHYA",
                      def: "SUBJECT_OPTIONS_BHASHYA",
                    })}
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {/* 4.4). Book Name*/}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_PT2_Q4_D'
                label={Text({
                  tid: "TEACHER_PT2_Q4_D",
                  def: "Book Name",
                })}
                rules={inputRequiredRule}
              >
                <Input
                  disabled={disabled}
                  onBlur={(e) =>
                    handleFieldChange("TEACHER_PT2_Q4_D", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 4.2). Select Year*/}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_PT2_Q4_B'
                label={Text({
                  tid: "TEACHER_PT2_Q4_B",
                  def: "Year",
                })}
                rules={inputRequiredRule}
              >
                <DatePicker
                  placeholder=''
                  disabled={disabled}
                  format='YYYY'
                  picker='year' // This ensures only years can be selected
                  onKeyDown={(e) => e.preventDefault()}
                  onChange={(_, dateString) => {
                    if (
                      typeof dateString === "string" &&
                      /^\d{4}$/.test(dateString)
                    ) {
                      handleFieldChange("TEACHER_PT2_Q4_B", dateString);
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 4.3). Examination Board*/}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_PT2_Q4_C'
                label={Text({
                  tid: "TEACHER_PT2_Q4_C",
                  def: "TEACHER_PT2_Q4_C",
                })}
                rules={inputRequiredRule}
              >
                <Input
                  disabled={disabled}
                  onBlur={(e) =>
                    handleFieldChange("TEACHER_PT2_Q4_C", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* 5. In the past five years, the number of your Ved students who passed the following exams */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q5",
                def: "Please provide details of all the education you have earned in the following subjects of the studied Vedas (see Ved Vidya Samakshata list):",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 5.1 - 5.5 : year, mulant, level1, level2, other, total  */}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q5'>
              <Table
                columns={TABLE_COLUMNS_TEACHER_PT2_Q5}
                dataSource={form.getFieldValue("TEACHER_PT2_Q5") || []}
                pagination={false}
                scroll={{ x: "80vw" }}
                size='large'
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 13. Annexure - 1: List of Ved students taught by you who passed Level-2 or higher exams during your lifetime */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q13",
                def: "Annexure - 1: List of Ved students taught by you who passed Level-2 or higher exams during your lifetime",
              })}
            </Typography.Text>
          </Col>
        </Row>

        {/* 13.1 - 13.4 : vedicStudent, levelOfStudy, examinationInstitution, year  */}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q13'>
              <Table<DATA_TYPE_TEACHER_PT2_Q13>
                columns={TABLE_COLUMNS_TEACHER_PT2_Q13}
                scroll={{ x: "80vw" }}
                size='large'
                dataSource={form.getFieldValue("TEACHER_PT2_Q13") || []}
                pagination={false}
                style={{ width: "100%" }}
              />
              {!disabled && (
                <Flex className='belowTableAddRemoveButton'>
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      const currentData =
                        form.getFieldValue("TEACHER_PT2_Q13") || [];
                      const newRow = {
                        vedicStudent: "",
                        levelOfStudy: "",
                        examinationInstitution: "",
                        year: "",
                      };
                      form.setFieldsValue({
                        TEACHER_PT2_Q13: [...currentData, newRow],
                      });
                      setRowCount((prev) => ({
                        ...prev,
                        TEACHER_PT2_Q13: prev.TEACHER_PT2_Q13 + 1,
                      }));
                    }}
                  >
                    {Text({
                      tid: "ADD",
                      def: "Add",
                    })}
                  </Button>
                </Flex>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* 14. Annexure - 2: List of Ved students currently studying under you */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q14",
                def: "6). Annexure - 2: List of Ved students currently studying under you",
              })}
            </Typography.Text>
          </Col>
        </Row>

        {/* 14.1 - 14.4 : nameOfVedicStudent, age, year, currentLevelOfEducation*/}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q14'>
              <Table<DATA_TYPE_TEACHER_PT2_Q14>
                columns={TABLE_COLUMNS_TEACHER_PT2_Q14}
                scroll={{ x: "80vw" }}
                size='large'
                dataSource={form.getFieldValue("TEACHER_PT2_Q14") || []}
                pagination={false}
                style={{ width: "100%" }}
              />
              {!disabled && (
                <Flex className='belowTableAddRemoveButton'>
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      const currentData =
                        form.getFieldValue("TEACHER_PT2_Q14") || [];
                      const newRow = {
                        nameOfVedicStudent: "",
                        age: "",
                        year: "",
                        currentLevelOfEducation: "",
                      };
                      form.setFieldsValue({
                        TEACHER_PT2_Q14: [...currentData, newRow],
                      });
                      setRowCount((prev) => ({
                        ...prev,
                        TEACHER_PT2_Q14: prev.TEACHER_PT2_Q14 + 1,
                      }));
                    }}
                  >
                    {Text({
                      tid: "ADD",
                      def: "Add",
                    })}
                  </Button>
                </Flex>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* 15. Annexure - 3: List of Ved students taught by you who are now teaching Vedas after completing their studies */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q15",
                def: "Annexure - 3: List of Ved students taught by you who are now teaching Vedas after completing their studies",
              })}
            </Typography.Text>
          </Col>
        </Row>

        {/* 15.1 - 15.3 : nameOfVedicStudent, year, currentlyTeachingVedaInstitution*/}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q15'>
              <Table<DATA_TYPE_TEACHER_PT2_Q15>
                columns={TABLE_COLUMNS_TEACHER_PT2_Q15}
                scroll={{ x: "80vw" }}
                size='large'
                dataSource={form.getFieldValue("TEACHER_PT2_Q15") || []}
                pagination={false}
                style={{ width: "100%" }}
              />
              {!disabled && (
                <Flex className='belowTableAddRemoveButton'>
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      const currentData =
                        form.getFieldValue("TEACHER_PT2_Q15") || [];
                      const newRow = {
                        nameOfVedicStudent: "",
                        year: "",
                        currentlyTeachingVedaInstitution: "",
                      };
                      form.setFieldsValue({
                        TEACHER_PT2_Q15: [...currentData, newRow],
                      });
                      setRowCount((prev) => ({
                        ...prev,
                        TEACHER_PT2_Q15: prev.TEACHER_PT2_Q15 + 1,
                      }));
                    }}
                  >
                    {Text({
                      tid: "ADD",
                      def: "Add",
                    })}
                  </Button>
                </Flex>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* 9. Attendance in Ved assemblies, seminars, workshops, etc. */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q9",
                def: "Attendance in Ved assemblies, seminars, workshops, etc.",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 9.1 - 9.3 :  Conference_or_Meeting, Organizer, year */}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q9'>
              <Table
                columns={TABLE_COLUMNS_TEACHER_PT2_Q9}
                dataSource={form.getFieldValue("TEACHER_PT2_Q9") || []}
                pagination={false}
                scroll={{ x: "80vw" }}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
              {!disabled && (
                <Flex className='belowTableAddRemoveButton'>
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      const currentData =
                        form.getFieldValue("TEACHER_PT2_Q9") || [];
                      const newRow = {
                        Conference_or_Meeting: "",
                        Organizer: "",
                        year: "",
                        TEACHER_PT2_Q9_DOC: "",
                      };
                      form.setFieldsValue({
                        TEACHER_PT2_Q9: [...currentData, newRow],
                      });
                      setRowCount((prev) => ({
                        ...prev,
                        TEACHER_PT2_Q9: prev.TEACHER_PT2_Q9 + 1,
                      }));
                    }}
                  >
                    {Text({ tid: "ADD", def: "Add" })}
                  </Button>
                </Flex>
              )}
            </Form.Item>
          </Col>
        </Row>
        {/* 10. Your activity in Ved Shrauta Yajnas */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q10",
                def: "Your activity in Ved Shrauta Yajnas",
              })}
            </Typography.Text>
          </Col>
        </Row>
        {/* 10.1 - 10.3 : yagyaWork, place, year  */}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q10'>
              <Table<DATA_TYPE_TEACHER_PT2_Q10>
                columns={TABLE_COLUMNS_TEACHER_PT2_Q10}
                scroll={{ x: "80vw" }}
                size='large'
                dataSource={form.getFieldValue("TEACHER_PT2_Q10") || []}
                pagination={false}
                style={{ width: "100%" }}
              />
              {!disabled && (
                <Flex className='belowTableAddRemoveButton'>
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      const currentData =
                        form.getFieldValue("TEACHER_PT2_Q10") || [];
                      const newRow = {
                        yagyaWork: "",
                        place: "",
                        year: "",
                        TEACHER_PT2_Q10_DOC: "",
                      };
                      form.setFieldsValue({
                        TEACHER_PT2_Q10: [...currentData, newRow],
                      });
                      setRowCount((prev) => ({
                        ...prev,
                        TEACHER_PT2_Q10: prev.TEACHER_PT2_Q10 + 1,
                      }));
                    }}
                  >
                    {Text({
                      tid: "ADD",
                      def: "Add",
                    })}
                  </Button>
                </Flex>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* 11. Details of awards or certificates of appreciation */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q11",
                def: "Details of awards or certificates of appreciation",
              })}
            </Typography.Text>
          </Col>
        </Row>

        {/* 11.1 - 11.3 : awardName, Presenter, year  */}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q11'>
              <Table<DATA_TYPE_TEACHER_PT2_Q11>
                columns={TABLE_COLUMNS_TEACHER_PT2_Q11}
                scroll={{ x: "80vw" }}
                size='large'
                dataSource={form.getFieldValue("TEACHER_PT2_Q11") || []}
                pagination={false}
                style={{ width: "100%" }}
              />
              {!disabled && (
                <Flex className='belowTableAddRemoveButton'>
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      const currentData =
                        form.getFieldValue("TEACHER_PT2_Q11") || [];
                      const newRow = {
                        awardName: "",
                        Presenter: "",
                        year: "",
                        TEACHER_PT2_Q11_DOC: "",
                      };
                      form.setFieldsValue({
                        TEACHER_PT2_Q11: [...currentData, newRow],
                      });
                      setRowCount((prev) => ({
                        ...prev,
                        TEACHER_PT2_Q11: prev.TEACHER_PT2_Q11 + 1,
                      }));
                    }}
                  >
                    {Text({
                      tid: "ADD",
                      def: "Add",
                    })}
                  </Button>
                </Flex>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* 12. Details of self-written books, articles, and research paper publications */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q12",
                def: "Details of self-written books, articles, and research paper publications",
              })}
            </Typography.Text>
          </Col>
        </Row>

        {/* 12.1 - 12.3 : awardName, Presenter, year  */}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q12'>
              <Table<DATA_TYPE_TEACHER_PT2_Q12>
                columns={TABLE_COLUMNS_TEACHER_PT2_Q12}
                scroll={{ x: "80vw" }}
                size='large'
                dataSource={form.getFieldValue("TEACHER_PT2_Q12") || []}
                pagination={false}
                style={{ width: "100%" }}
              />
              {!disabled && (
                <Flex className='belowTableAddRemoveButton'>
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      const currentData =
                        form.getFieldValue("TEACHER_PT2_Q12") || [];
                      const newRow = {
                        title: "",
                        publisher: "",
                        isbn: "",
                        year: "",
                        TEACHER_PT2_Q12_DOC: "",
                      };
                      form.setFieldsValue({
                        TEACHER_PT2_Q12: [...currentData, newRow],
                      });
                      setRowCount((prev) => ({
                        ...prev,
                        TEACHER_PT2_Q12: prev.TEACHER_PT2_Q12 + 1,
                      }));
                    }}
                  >
                    {Text({
                      tid: "ADD",
                      def: "Add",
                    })}
                  </Button>
                </Flex>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* 16. Details of documents attached with this application:*/}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q16",
                def: "Details of documents attached with this application:",
              })}
            </Typography.Text>
            <Typography.Text>
              {Text({
                tid: "TEACHER_PT2_Q16_A",
                def: "Details of documents attached with this application:",
              })}
            </Typography.Text>
          </Col>
        </Row>

        {/* 16.1 : documentDetails*/}
        <Row>
          <Col xs={24}>
            <Form.Item name='TEACHER_PT2_Q16'>
              <Table<DATA_TYPE_TEACHER_PT2_Q16>
                columns={TABLE_COLUMNS_TEACHER_PT2_Q16}
                scroll={{ x: "80vw" }}
                size='large'
                dataSource={getUploadedDocuments()}
                pagination={false}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* -------------------------------------------- */}
        {/* TEACHER_P2_AFFIDAVIT_TITLE */}
        <div
          id='tch_form2_applicant_affidavit'
          className='affidavitSection'
        >
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "TEACHER_P2_AFFIDAVIT_TITLE",
                  def: "Applicant affidavit",
                })}
              </Typography.Text>
            </Col>
          </Row>
          {/* TEACHER_P2_I */}
          <Row className='affidavitRow'>
            <Col xs={24}>
              <Typography.Text type='secondary'>
                {Text({
                  tid: "TEACHER_P2_AFFIDAVIT_I",
                  def: "TEACHER_P2_AFFIDAVIT_I",
                })}
              </Typography.Text>
              <Form.Item
                name='TEACHER_PT2_Q17'
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
                className='affidavitRowInput'
              >
                <Input
                  id={applyPdfStyles ? "affidavit_input" : ""}
                  disabled={applyPdfStyles ? false : true}
                  onBlur={(e) =>
                    handleFieldChange("TEACHER_PT2_Q17", e.target.value)
                  }
                />
              </Form.Item>
              <Typography.Text type='secondary'>
                {Text({
                  tid: "TEACHER_P2_AFFIDAVIT_LINE_1",
                  def: "TEACHER_P2_AFFIDAVIT_LINE_1",
                })}
              </Typography.Text>

              {/* <Form.Item
              name='TEACHER_PT2_Q18'
              style={{
                maxWidth: "200px",
                position: "relative",
                display: "inline",
              }}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                id='affidavit_input'
                onBlur={(e) =>
                  handleFieldChange("TEACHER_PT2_Q18", e.target.value)
                }
              />
            </Form.Item> */}
            </Col>
          </Row>

          <ul>
            {/* TEACHER AFFIDAVIT DESCRIPTION */}
            <li>
              <Row>
                <Col>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "TEACHER_P2_AFFIDAVIT_CLAUSE_1",
                      def: "TEACHER_P2_AFFIDAVIT_CLAUSE_1",
                    })}
                  </Typography.Text>
                </Col>
              </Row>
            </li>
            <li>
              <Row className='affidavitRow'>
                <Col xs={24}>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "TEACHER_P2_AFFIDAVIT_CLAUSE_21",
                      def: "TEACHER_P2_AFFIDAVIT_CLAUSE_21",
                    })}
                  </Typography.Text>
                  <Form.Item
                    name='TEACHER_PT2_Q18'
                    rules={[
                      {
                        required: true,
                        message: Text({ tid: "REQUIRED", def: "Required" }),
                      },
                    ]}
                    className='affidavitRowInput'
                  >
                    <Input
                      id={applyPdfStyles ? "affidavit_input" : ""}
                      disabled={disabled}
                      onBlur={(e) =>
                        handleFieldChange("TEACHER_PT2_Q18", e.target.value)
                      }
                    />
                  </Form.Item>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "TEACHER_P2_AFFIDAVIT_CLAUSE_22",
                      def: "TEACHER_P2_AFFIDAVIT_CLAUSE_22",
                    })}
                  </Typography.Text>
                </Col>
              </Row>
            </li>

            <Row>
              <Col>
                <li>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "TEACHER_P2_AFFIDAVIT_CLAUSE_3",
                      def: "TEACHER_P2_AFFIDAVIT_CLAUSE_3",
                    })}
                  </Typography.Text>
                </li>
                <li>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "TEACHER_P2_AFFIDAVIT_CLAUSE_4",
                      def: "TEACHER_P2_AFFIDAVIT_CLAUSE_4",
                    })}
                  </Typography.Text>
                </li>
              </Col>
            </Row>
          </ul>
          <div>
            <Typography.Text type='secondary'>
              {Text({
                tid: "TEACHER_P2_AFFIDAVIT_VERIFICATION",
                def: "TEACHER_P2_AFFIDAVIT_VERIFICATION",
              })}
            </Typography.Text>

            <Typography.Text type='secondary'>
              {Text({
                tid: "TEACHER_P2_AFFIDAVIT_UNDERSTANDING",
                def: "TEACHER_P2_AFFIDAVIT_UNDERSTANDING",
              })}
            </Typography.Text>
            <Typography.Text type='secondary'>
              {Text({
                tid: "TEACHER_P2_AFFIDAVIT_ACCEPTANCE",
                def: "TEACHER_P2_AFFIDAVIT_ACCEPTANCE",
              })}
            </Typography.Text>
          </div>
          {/* NAME */}
          <Row className='QuestionsSubSection affidavitQuestionFirst'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "TEACHER_P2_APPLICANT_FULL_NAME",
                      def: "TEACHER_P2_APPLICANT_FULL_NAME",
                    })}
                  </span>
                  <Form.Item
                    name='TEACHER_PT2_Q20'
                    rules={[
                      {
                        required: true,
                        message: Text({ tid: "REQUIRED", def: "Required" }),
                      },
                    ]}
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      disabled={applyPdfStyles ? false : true}
                      onBlur={(e) =>
                        handleFieldChange("TEACHER_PT2_Q20", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='TEACHER_PT2_Q20'
                  label={Text({
                    tid: "TEACHER_P2_APPLICANT_FULL_NAME",
                    def: "TEACHER_P2_APPLICANT_FULL_NAME",
                  })}
                  rules={[
                    {
                      required: true,
                      message: Text({ tid: "REQUIRED", def: "Required" }),
                    },
                  ]}
                >
                  <Input
                    disabled={applyPdfStyles ? false : true}
                    onBlur={(e) =>
                      handleFieldChange("TEACHER_PT2_Q20", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
          {/* LOCATION */}
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "TEACHER_P2_APPLICANT_PLACE",
                      def: "TEACHER_P2_APPLICANT_PLACE",
                    })}
                  </span>
                  <Form.Item
                    name='TEACHER_PT2_Q21'
                    rules={[
                      {
                        required: true,
                        message: Text({ tid: "REQUIRED", def: "Required" }),
                      },
                    ]}
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      disabled={disabled}
                      onBlur={(e) =>
                        handleFieldChange("TEACHER_PT2_Q21", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='TEACHER_PT2_Q21'
                  label={Text({
                    tid: "TEACHER_P2_APPLICANT_PLACE",
                    def: "TEACHER_P2_APPLICANT_PLACE",
                  })}
                  rules={[
                    {
                      required: true,
                      message: Text({ tid: "REQUIRED", def: "Required" }),
                    },
                  ]}
                >
                  <Input
                    disabled={disabled}
                    onBlur={(e) =>
                      handleFieldChange("TEACHER_PT2_Q21", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
          {/* DATE */}
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              {applyPdfStyles ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "TEACHER_P2_APPLICANT_DATE",
                      def: "TEACHER_P2_APPLICANT_DATE",
                    })}
                  </span>
                  <Form.Item
                    name='TEACHER_PT2_Q22'
                    rules={[
                      {
                        required: true,
                        message: Text({ tid: "REQUIRED", def: "Required" }),
                      },
                    ]}
                    style={{ marginBottom: 0, flex: 1 }}
                  >
                    <Input
                      type={applyPdfStyles ? undefined : "date"}
                      disabled={disabled}
                      max={disableFutureDates_on_input_date_max()}
                      onChange={(e) =>
                        handleFieldChange("TEACHER_PT2_Q22", e.target.value)
                      }
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name='TEACHER_PT2_Q22'
                  label={Text({
                    tid: "TEACHER_P2_APPLICANT_DATE",
                    def: "TEACHER_P2_APPLICANT_DATE",
                  })}
                  rules={[
                    {
                      required: true,
                      message: Text({ tid: "REQUIRED", def: "Required" }),
                    },
                  ]}
                >
                  <Input
                    type={applyPdfStyles ? undefined : "date"}
                    disabled={disabled}
                    max={disableFutureDates_on_input_date_max()}
                    onChange={(e) =>
                      handleFieldChange("TEACHER_PT2_Q22", e.target.value)
                    }
                    id={applyPdfStyles ? "affidavit_input" : ""}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {applyPdfStyles ? (
            <Row className='QuestionsSubSection'>
              <Col
                xs={24}
                lg={12}
                xl={8}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: "max-content",
                  }}
                >
                  <span style={{ minWidth: "max-content" }}>
                    {Text({
                      tid: "SIGNATURE_OF_APPLICANT",
                      def: "Signature of applicant",
                    })}
                  </span>
                  <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                    <Input id={applyPdfStyles ? "affidavit_input" : ""} />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          ) : null}
        </div>
        <div className='affidavitSection'>
          {!disabled && (
            <Row className='mb-15 QuestionsSubSection'>
              <Button
                type='default'
                onClick={() =>
                  handleGeneratePDF(
                    "tch_form2_applicant_affidavit",
                    "teacher_form2_applicant_affidavit"
                  )
                }
                className='downloadAffidavitButton'
              >
                {Text({
                  tid: "DOWNLOAD_AFFIDAVIT",
                  def: "Download affidavit",
                })}
              </Button>
            </Row>
          )}

          {/* TEACHER AFFIDAVIT SIGNATURE */}
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_PT2_Q19'
                label={Text({
                  tid: "UPLOAD_AFFIDAVIT",
                  def: "Upload affidavit",
                })}
                rules={[
                  {
                    required:
                      signatureId ||
                      (documentsData?.length &&
                        documentsData.find(
                          (doc: any) => doc.labelName === "TEACHER_PT2_Q19"
                        ))
                        ? false
                        : true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled}
                  type='file'
                  // onChange={(e) => {
                  //   if (e.target.files && e.target.files[0]) {
                  //     uploadDocument(
                  //       e.target.files[0],
                  //       eDocType.OTHER,
                  //       "TEACHER_PT2_Q19"
                  //     );
                  //   }
                  // }}
                  accept='.jpg,.jpeg,.png,.pdf'
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!validateFile(file as File).valid) {
                      onShowToast({
                        type: ToastType.error,
                        title: <CloseOutlined />,
                        position: ToastOpen.leftBottom,
                        content: validateFile(file as File).error,
                      });
                      e.target.value = "";
                      form.resetFields(["TEACHER_PT2_Q19"]);
                    } else if (file) {
                      uploadDocument(file, eDocType.OTHER, "TEACHER_PT2_Q19");
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              {" "}
              {signatureId ? (
                <div className='previewLink'>
                  <span>
                    {Text({
                      tid: "ATTACHED_DOCUMENT",
                      def: "Attached Document",
                    })}
                    :
                  </span>
                  <a
                    href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${signatureId}`}
                    target='_blank'
                    rel='noreferrer'
                    className='primary-link'
                  >
                    {PREVIEW}
                  </a>
                </div>
              ) : (
                documentsData &&
                documentsData.length > 0 &&
                documentsData
                  .filter((doc: any) => doc.labelName === "TEACHER_PT2_Q19")
                  .map((doc: any) => (
                    <div className='previewLink'>
                      <span>
                        {Text({
                          tid: "ATTACHED_DOCUMENT",
                          def: "Attached Document",
                        })}
                        :
                      </span>
                      <a
                        href={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${doc.docPath}`}
                        target='_blank'
                        rel='noreferrer'
                        className='primary-link'
                      >
                        {doc.docName}
                      </a>
                    </div>
                  ))
              )}
            </Col>
          </Row>
        </div>

        {/* Hidden validation fields for Q11 document requirement */}
        {form.getFieldValue("TEACHER_PT2_Q11")?.map((_: any, index: number) => {
          const docUrl = form.getFieldValue([
            "TEACHER_PT2_Q11",
            index,
            "TEACHER_PT2_Q11_DOC",
          ]);
          return (
            <Form.Item
              key={`q11-validation-${index}`}
              name={["TEACHER_PT2_Q11", index, "TEACHER_PT2_Q11_DOC"]}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
              style={{ display: "none" }}
            >
              <input type='hidden' />
            </Form.Item>
          );
        })}

        {/* -------------------------------------------- */}
        {/* SUBMIT BUTTON */}
        <Row
          style={
            disabled === true
              ? { marginTop: "var(--bs-item-negative-margin-30)" }
              : undefined
          }
        >
          {disabled === false && (
            <Button
              block
              type='primary'
              // variant="filled"
              htmlType='submit'
              disabled={isLoading || isSubmitting || isUploading}
              className='formSubmitButton'
              loading={isLoading || isSubmitting}
            >
              {Text({ tid: "SUBMIT", def: "SUBMIT" })}
            </Button>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default TeacherPartTwo;
